/**
 * Hook for detecting meeting scheduling conflicts and adjacent meetings.
 *
 * Debounces the proposed start/end times, fetches nearby meetings from the API,
 * expands recurring meetings, and classifies results as overlapping, before, or after.
 *
 * @module hooks/useScheduleConflicts
 */

import { useCallback, useEffect, useRef, useMemo, useReducer } from 'react'
import { useDebounce } from './useDebounce'
import { dashboardAPI } from '../services/api'
import { expandRecurringMeetings } from '../utils/expandRecurringMeetings'

function reducer(state, action) {
  switch (action.type) {
    case 'FETCH_START':
      return { ...state, loading: true }
    case 'FETCH_SUCCESS':
      return { loading: false, meetings: action.meetings }
    case 'FETCH_ERROR':
    case 'RESET':
      return { loading: false, meetings: [] }
    default:
      return state
  }
}

/**
 * @param {string|undefined} startTimeIso - Proposed meeting start (UTC ISO string)
 * @param {string|undefined} endTimeIso   - Proposed meeting end (UTC ISO string)
 * @param {string|undefined} excludeMeetingId - Meeting being edited (exclude from conflicts)
 * @returns {{
 *   loading: boolean,
 *   overlapping: Array<{meetingId: string, title: string, scheduledStartTime: string, scheduledEndTime: string}>,
 *   meetingBefore: {meetingId: string, title: string, scheduledStartTime: string, scheduledEndTime: string}|null,
 *   meetingAfter: {meetingId: string, title: string, scheduledStartTime: string, scheduledEndTime: string}|null,
 *   hasOverlap: boolean
 * }}
 */
export function useScheduleConflicts(startTimeIso, endTimeIso, excludeMeetingId) {
  const debouncedStart = useDebounce(startTimeIso, 500)
  const debouncedEnd = useDebounce(endTimeIso, 500)

  const [state, dispatch] = useReducer(reducer, { loading: false, meetings: [] })
  const requestIdRef = useRef(0)

  const fetchConflicts = useCallback((start, end, exclude) => {
    const currentRequestId = ++requestIdRef.current
    dispatch({ type: 'FETCH_START' })

    dashboardAPI
      .getMeetingConflicts(start, end, exclude || undefined)
      .then((res) => {
        if (requestIdRef.current !== currentRequestId) return
        const meetings = res?.data?.meetings ?? res?.meetings ?? []
        dispatch({ type: 'FETCH_SUCCESS', meetings })
      })
      .catch(() => {
        if (requestIdRef.current !== currentRequestId) return
        dispatch({ type: 'FETCH_ERROR' })
      })
  }, [])

  useEffect(() => {
    if (!debouncedStart || !debouncedEnd) {
      requestIdRef.current++
      dispatch({ type: 'RESET' })
      return
    }
    fetchConflicts(debouncedStart, debouncedEnd, excludeMeetingId)
  }, [debouncedStart, debouncedEnd, excludeMeetingId, fetchConflicts])

  const result = useMemo(() => {
    if (!debouncedStart || !debouncedEnd || state.meetings.length === 0) {
      return { overlapping: [], meetingBefore: null, meetingAfter: null, hasOverlap: false }
    }

    const proposedStart = new Date(debouncedStart).getTime()
    const proposedEnd = new Date(debouncedEnd).getTime()

    // Expand recurring meetings into the ±4h window
    const windowStart = new Date(proposedStart - 4 * 60 * 60 * 1000)
    const windowEnd = new Date(proposedEnd + 4 * 60 * 60 * 1000)
    const expanded = expandRecurringMeetings(state.meetings, windowStart, windowEnd)

    const overlapping = []
    let closestBefore = null
    let closestAfter = null

    for (const m of expanded) {
      const mStart = new Date(m.scheduledStartTime || m.startTime).getTime()
      const mEnd = new Date(m.scheduledEndTime || m.endTime).getTime()

      if (Number.isNaN(mStart) || Number.isNaN(mEnd)) continue

      if (mStart < proposedEnd && mEnd > proposedStart) {
        overlapping.push(m)
      } else if (mEnd <= proposedStart) {
        if (!closestBefore || mEnd > new Date(closestBefore.scheduledEndTime || closestBefore.endTime).getTime()) {
          closestBefore = m
        }
      } else if (mStart >= proposedEnd) {
        if (!closestAfter || mStart < new Date(closestAfter.scheduledStartTime || closestAfter.startTime).getTime()) {
          closestAfter = m
        }
      }
    }

    // Only surface adjacent meetings if the gap is ≤ 15 minutes
    const ADJACENT_THRESHOLD_MS = 15 * 60 * 1000
    const nearBefore = closestBefore
      ? (proposedStart - new Date(closestBefore.scheduledEndTime || closestBefore.endTime).getTime()) <= ADJACENT_THRESHOLD_MS
        ? closestBefore
        : null
      : null
    const nearAfter = closestAfter
      ? (new Date(closestAfter.scheduledStartTime || closestAfter.startTime).getTime() - proposedEnd) <= ADJACENT_THRESHOLD_MS
        ? closestAfter
        : null
      : null

    return {
      overlapping,
      meetingBefore: nearBefore,
      meetingAfter: nearAfter,
      hasOverlap: overlapping.length > 0,
    }
  }, [debouncedStart, debouncedEnd, state.meetings])

  return { loading: state.loading, ...result }
}

export default useScheduleConflicts
